import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API_BASE_URL from 'src/constants';

// NOTE: Once Phase 1 BE is live and `yarn generate:api-code` has been run
// against the upgraded Odoo, migrate these manual types to
// `components['schemas']` and the hooks to `$api.useQuery(...)`. The endpoints
// already exist on the BE; see addon `df_forecast` + API surface
// `df_clientportal_api/api/forecast/`.

export type ForecastState = 'draft' | 'submitted' | 'approved' | 'rejected';

export type WeightingPreset = 'realistic' | 'equal' | 'custom';

export interface ForecastWeights {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

export const PRESET_WEIGHTS: Record<Exclude<WeightingPreset, 'custom'>, ForecastWeights> = {
  realistic: { mon: 1.2, tue: 1.0, wed: 1.0, thu: 1.0, fri: 0.9, sat: 0.8, sun: 1.1 },
  equal: { mon: 1.0, tue: 1.0, wed: 1.0, thu: 1.0, fri: 1.0, sat: 1.0, sun: 1.0 },
};

export const DEFAULT_CUTOFF_BEFORE_PCT = 30;

export interface CustomerForecastWizardIn {
  total_packets: number;
  weights?: ForecastWeights;
  preset?: WeightingPreset;
  cutoff_before_pct?: number;
}

export interface CustomerForecastSummary {
  id: number;
  partner_id: number;
  partner_name: string;
  year: number;
  month: number;
  state: ForecastState;
  total_pakete: number;
  total_pakete_boosted: number;
  input_total_packets?: number | null;
  total_actual: number;
  total_possible: number;
  actuals_filled_days: number;
  deadline?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  workdays_in_month: number;
  is_overdue: boolean;
  manually_added?: boolean;
}

export interface AppliedPromotion {
  id: number;
  name: string;
  kind: 'multiplier' | 'additional';
  value: number;
}

export interface CustomerForecastLine {
  day: number;
  pakete: number;
  pakete_boosted?: number | null;
  applied_promotions?: AppliedPromotion[];
  actual_quantity?: number | null;
  todo_quantity?: number | null;
  possible?: number | null;
  delta_quantity?: number | null;
}

export interface CustomerForecast extends CustomerForecastSummary {
  days_in_month: number;
  rejection_reason?: string | null;
  lines: CustomerForecastLine[];
  input_total_packets?: number | null;
  weighting_preset?: WeightingPreset | null;
  weights?: ForecastWeights | null;
  cutoff_before_pct?: number | null;
  carry_in_packets?: number | null;
}

export interface CustomerForecastUpdate {
  lines: CustomerForecastLine[];
}

export type ChangeRequestState = 'draft' | 'submitted' | 'approved' | 'rejected';

export interface CustomerForecastChangeRequest {
  id: number;
  forecast_id: number;
  reason: string;
  state: ChangeRequestState;
  submitted_at?: string | null;
  decided_at?: string | null;
  decision_reason?: string | null;
}

export interface CustomerForecastChangeRequestCreate {
  reason: string;
}

const PORTAL = `${API_BASE_URL}portal`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()) as T;
}

export const forecastsApi = {
  listOwn: () =>
    fetch(`${PORTAL}/customer-forecasts`, { headers: authHeaders() }).then(asJson<CustomerForecastSummary[]>),

  getOrCreateByMonth: (year: number, month: number) =>
    fetch(`${PORTAL}/customer-forecasts/by-month/${year}/${month}`, {
      headers: authHeaders(),
    }).then(asJson<CustomerForecast>),

  getById: (id: number) =>
    fetch(`${PORTAL}/customer-forecasts/${id}`, { headers: authHeaders() }).then(asJson<CustomerForecast>),

  update: (id: number, payload: CustomerForecastUpdate) =>
    fetch(`${PORTAL}/customer-forecasts/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecast>),

  submit: (id: number) =>
    fetch(`${PORTAL}/customer-forecasts/${id}/submit`, {
      method: 'POST',
      headers: authHeaders(),
    }).then(asJson<CustomerForecast>),

  cancel: (id: number) =>
    fetch(`${PORTAL}/customer-forecasts/${id}/cancel`, {
      method: 'POST',
      headers: authHeaders(),
    }).then(asJson<CustomerForecast>),

  saveWizard: (id: number, payload: CustomerForecastWizardIn) =>
    fetch(`${PORTAL}/customer-forecasts/${id}/wizard`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecast>),

  requestChange: (id: number, payload: CustomerForecastChangeRequestCreate) =>
    fetch(`${PORTAL}/customer-forecasts/${id}/change-request`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecastChangeRequest>),
};

// ── React Query hooks ──────────────────────────────────────────────────────

const QK = {
  list: ['forecasts', 'list'] as const,
  byMonth: (year: number, month: number) => ['forecasts', 'byMonth', year, month] as const,
  byId: (id: number | undefined) => ['forecasts', 'byId', id] as const,
};

export const useOwnForecasts = () =>
  useQuery({
    queryKey: QK.list,
    queryFn: forecastsApi.listOwn,
  });

export const useForecastByMonth = (year: number | undefined, month: number | undefined, enabled = true) =>
  useQuery({
    queryKey: QK.byMonth(year ?? 0, month ?? 0),
    queryFn: () => forecastsApi.getOrCreateByMonth(year as number, month as number),
    enabled: enabled && !!year && !!month,
  });

export const useForecast = (id: number | undefined) =>
  useQuery({
    queryKey: QK.byId(id),
    queryFn: () => forecastsApi.getById(id as number),
    enabled: !!id,
  });

// Imperative get-or-create for a chosen month ("Add Month"). The backend
// pins manually-added months (they survive the out-of-horizon cleanup and
// skip the deadline/overdue rule for the current + gap month).
export const useAddForecastMonth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) => forecastsApi.getOrCreateByMonth(year, month),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.setQueryData(QK.byId(data.id), data);
    },
  });
};

export const useUpdateForecast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CustomerForecastUpdate }) => forecastsApi.update(id, payload),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.setQueryData(QK.byId(data.id), data);
    },
  });
};

export const useSubmitForecast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => forecastsApi.submit(id),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.setQueryData(QK.byId(data.id), data);
    },
  });
};

export const useCancelForecast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => forecastsApi.cancel(id),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.setQueryData(QK.byId(data.id), data);
    },
  });
};

export const useSaveForecastWizard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CustomerForecastWizardIn }) =>
      forecastsApi.saveWizard(id, payload),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      queryClient.setQueryData(QK.byId(data.id), data);
    },
  });
};

export const useRequestForecastChange = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CustomerForecastChangeRequestCreate }) =>
      forecastsApi.requestChange(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
    },
  });
};

export const STATE_LABEL_KEY: Record<ForecastState, string> = {
  draft: 'forecasts.state.draft',
  submitted: 'forecasts.state.submitted',
  approved: 'forecasts.state.approved',
  rejected: 'forecasts.state.rejected',
};
